import { useState } from 'react'
import {
  Plus,
  X,
  RefreshCw,
  Search,
  UserCircle,
  Clock,
  CheckCircle2,
  AlertTriangle,
} from 'lucide-react'
import { toast } from 'sonner'
import { formatDistanceToNow } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { SectionCard } from '@/components/shared/section-card'
import { FormSection } from '@/components/shared/form-section'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  useListeningConfig,
  useTriggerSync,
  useSyncJobs,
  useEnrichProfiles,
  useApifySync,
} from '@/hooks/use-social-listening'

interface MonitoringSectionProps {
  isDirty: boolean
  hashtags: string[]
  setHashtags: (h: string[]) => void
  syncInterval: string
  setSyncInterval: (v: string) => void
  moderationEnabled: boolean
  setModerationEnabled: (v: boolean) => void
}

export function MonitoringSection({
  isDirty: _isDirty,
  hashtags,
  setHashtags,
  syncInterval,
  setSyncInterval,
  moderationEnabled,
  setModerationEnabled,
}: MonitoringSectionProps) {
  const { data: config } = useListeningConfig()
  const { data: jobs } = useSyncJobs()
  const triggerSync = useTriggerSync()
  const apifySync = useApifySync()
  const enrichProfiles = useEnrichProfiles()

  const [newHashtag, setNewHashtag] = useState('')

  const addHashtag = () => {
    const clean = newHashtag.replace(/^@/, '').trim().toLowerCase()
    if (!clean) return
    if (hashtags.includes(clean)) {
      toast.error('Perfil já adicionado')
      return
    }
    setHashtags([...hashtags, clean])
    setNewHashtag('')
  }

  const removeHashtag = (tag: string) => {
    setHashtags(hashtags.filter((h) => h !== tag))
  }

  const handleSync = async () => {
    try {
      await triggerSync.mutateAsync()
      toast.success('Sincronização iniciada')
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao sincronizar'
      toast.error(message)
    }
  }

  const handleApifySync = async () => {
    try {
      const result = await apifySync.mutateAsync()
      if (result.newCount > 0) {
        toast.success(
          `${result.newCount} novos posts encontrados via Apify (${result.totalScraped} analisados)`
        )
      } else {
        toast.info(`Nenhum post novo encontrado (${result.totalScraped} analisados)`)
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro na reconciliação Apify'
      toast.error(message)
    }
  }

  const handleEnrich = async () => {
    try {
      const result = await enrichProfiles.mutateAsync()
      toast.success(`${result.enriched} perfis enriquecidos, ${result.skipped} ignorados`)
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao enriquecer perfis'
      toast.error(message)
    }
  }

  const hasHashtags = hashtags.length > 0
  const lastSync = config?.lastSyncAt ? new Date(config.lastSyncAt) : null
  const latestJob = jobs && jobs.length > 0 ? jobs[0] : null
  const isRunning = latestJob?.status === 'running'

  return (
    <SectionCard title="Monitoramento" description="Configure perfis e sincronização.">
      <div className="divide-y divide-border/50">
        <FormSection title="Perfis monitorados">
          <div className="space-y-3">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  @
                </span>
                <Input
                  value={newHashtag}
                  onChange={(e) => setNewHashtag(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addHashtag())}
                  placeholder="nuture.com.br"
                  className="pl-7"
                />
              </div>
              <Button variant="outline" size="icon" onClick={addHashtag}>
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {hashtags.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {hashtags.map((tag) => (
                  <Badge key={tag} variant="secondary" className="gap-1 pr-1">
                    @{tag}
                    <button
                      onClick={() => removeHashtag(tag)}
                      className="ml-1 rounded hover:bg-muted p-0.5"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </FormSection>

        <FormSection title="Sincronização">
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Intervalo de sincronização</Label>
              <Select value={syncInterval} onValueChange={setSyncInterval}>
                <SelectTrigger className="w-48">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="6">A cada 6 horas</SelectItem>
                  <SelectItem value="12">A cada 12 horas</SelectItem>
                  <SelectItem value="24">A cada 24 horas</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {lastSync ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4 shrink-0" />
                Última sincronização{' '}
                {formatDistanceToNow(lastSync, { addSuffix: true, locale: ptBR })}
                {latestJob && (
                  <>
                    {latestJob.status === 'completed' && (
                      <Badge variant="default" className="gap-1 ml-1">
                        <CheckCircle2 className="h-3 w-3" />
                        {latestJob.newMentions} novas
                      </Badge>
                    )}
                    {latestJob.status === 'failed' && (
                      <Badge variant="destructive" className="gap-1 ml-1">
                        <AlertTriangle className="h-3 w-3" />
                        Falha
                      </Badge>
                    )}
                    {isRunning && (
                      <Badge variant="secondary" className="gap-1 ml-1">
                        <RefreshCw className="h-3 w-3 animate-spin" />
                        Em andamento
                      </Badge>
                    )}
                  </>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Nenhuma sincronização realizada.</p>
            )}

            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
                onClick={handleSync}
                disabled={isRunning || !hasHashtags || triggerSync.isPending}
              >
                <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${isRunning ? 'animate-spin' : ''}`} />
                Sincronizar agora
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
                onClick={handleApifySync}
                disabled={!hasHashtags || apifySync.isPending}
              >
                <Search className="h-3.5 w-3.5 mr-1.5" />
                Reconciliar Apify
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="w-full sm:w-auto"
                onClick={handleEnrich}
                disabled={enrichProfiles.isPending}
              >
                <UserCircle className="h-3.5 w-3.5 mr-1.5" />
                Enriquecer perfis
              </Button>
            </div>
          </div>
        </FormSection>

        <FormSection title="Moderação de comentários">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label>Moderação automática</Label>
              <p className="text-sm text-muted-foreground">
                Ocultar automaticamente comentários negativos nos seus posts do Instagram.
              </p>
            </div>
            <Switch checked={moderationEnabled} onCheckedChange={setModerationEnabled} />
          </div>
        </FormSection>
      </div>
    </SectionCard>
  )
}
